import { MESSAGES } from '~/utils/message';

const { CREATE } = MESSAGES.SETTINGS.WEBHOOKS;

// Keys match the contact fields the webhook API accepts in `contactFields`.
const CONTACT_FIELD_OPTIONS = [
  { field: 'firstName', title: CREATE.ADDITIONAL_FIELD_FIRST_NAME },
  { field: 'lastName', title: CREATE.ADDITIONAL_FIELD_LAST_NAME },
  { field: 'companyName', title: CREATE.ADDITIONAL_FIELD_COMPANY_NAME },
];

const CONTACT_PREFIX = 'contact:';
const CUSTOM_PREFIX = 'custom:';

/**
 * One list for the dropdown: the contact fields first, then the account's custom fields.
 * Option ids are prefixed strings so a contact field can never collide with a custom field id.
 * `sampleKey` is the key the field has in a delivered payload: the API name for a contact field,
 * the field's own name for a custom field.
 */
export const buildAdditionalFieldOptions = (customFields = []) => [
  ...CONTACT_FIELD_OPTIONS.map(({ field, title }) => ({
    id: `${CONTACT_PREFIX}${field}`,
    title,
    sampleKey: field,
  })),
  ...customFields.map(({ id, value }) => ({
    id: `${CUSTOM_PREFIX}${id}`,
    title: value,
    sampleKey: value,
  })),
];

/** Splits the selected option ids into the `contactFields` / `customFieldIds` the API expects. */
export const toAdditionalFields = (selectedIds = []) => ({
  contactFields: selectedIds
    .filter((id) => id.startsWith(CONTACT_PREFIX))
    .map((id) => id.slice(CONTACT_PREFIX.length)),
  customFieldIds: selectedIds
    .filter((id) => id.startsWith(CUSTOM_PREFIX))
    .map((id) => Number(id.slice(CUSTOM_PREFIX.length))),
});

/** Selected option ids for a saved webhook, exactly as stored. */
export const toSelectedFieldIds = (webhook) => [
  ...(webhook?.contactFields || []).map((field) => `${CONTACT_PREFIX}${field}`),
  ...(webhook?.customFieldIds || []).map((id) => `${CUSTOM_PREFIX}${id}`),
];

/** Labels of a saved webhook's additional fields, skipping custom fields that were deleted. */
export const getSelectedFieldLabels = (webhook, options) => {
  const availableIds = keepAvailableFieldIds(toSelectedFieldIds(webhook), options);

  return options.filter(({ id }) => availableIds.includes(id)).map(({ title }) => title);
};

/**
 * Drops ids with no matching option, such as a deleted custom field. Filtering here rather than
 * when the webhook loads keeps saved fields intact if the custom field list arrives later.
 */
export const keepAvailableFieldIds = (selectedIds, options) => {
  const availableIds = new Set(options.map(({ id }) => id));

  return selectedIds.filter((id) => availableIds.has(id));
};

const SAMPLE_CUSTOM_FIELD_VALUE = 'Sample value';

// Where each event carries its contact; every block names the custom fields object `custom_fields`.
const SAMPLE_CUSTOM_FIELD_PLACEMENTS = {
  'order_items.updated': (data, customFields) =>
    Array.isArray(data)
      ? data.map((item) => ({ ...item, contact: { ...item.contact, custom_fields: customFields } }))
      : data,
  'order_item.qr_code_scanned': (data, customFields) => ({
    ...data,
    scanned_by: { ...data.scanned_by, custom_fields: customFields },
  }),
  'order_item.purl_visited': (data, customFields) => ({
    ...data,
    visited_by: { ...data.visited_by, custom_fields: customFields },
  }),
};

/** Payload keys of every field ticked in the dropdown, contact fields and custom fields alike. */
export const getSelectedFieldKeys = (selectedIds, options) =>
  options.filter(({ id }) => selectedIds.includes(id)).map(({ sampleKey }) => sampleKey);

/**
 * Adds a `custom_fields` object to an event's sample: one entry per selected field, or an empty
 * object when nothing is selected. Events with no contact block are returned unchanged.
 */
export const addCustomFieldsToSample = (eventName, data, fieldKeys) => {
  const addToSample = SAMPLE_CUSTOM_FIELD_PLACEMENTS[eventName];
  if (!addToSample) return data;

  const customFields = Object.fromEntries(fieldKeys.map((key) => [key, SAMPLE_CUSTOM_FIELD_VALUE]));

  return addToSample(data, customFields);
};
